import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("smartai_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("smartai_token");
      localStorage.removeItem("smartai_user");
      // Don't bounce visitors off marketing / legal / public routes — they
      // legitimately render without a session. Same logic as PUBLIC_ROUTES
      // in AuthContext: home, pricing, support, privacy, terms, auth flows,
      // and public game share pages.
      const p = window.location.pathname;
      const isPublic =
        p === "/" ||
        p.startsWith("/auth") ||
        p.startsWith("/pricing") ||
        p.startsWith("/support") ||
        p.startsWith("/privacy") ||
        p.startsWith("/terms") ||
        p.startsWith("/forgot-password") ||
        p.startsWith("/reset-password") ||
        p.startsWith("/play/");
      if (!isPublic) {
        window.location.href = "/auth";
      }
    }
    // Auto-retry transient network / cold-start failures (no response, 502/503/504).
    // Common right after a deploy when the service worker still has stale routes
    // or the container is still booting. Retries up to 3 times with exponential backoff.
    const cfg = err?.config;
    if (cfg) {
      cfg.__retryCount = cfg.__retryCount || 0;
      const code = err?.response?.status;
      const isTransient = !err.response || code === 502 || code === 503 || code === 504;
      const method = (cfg.method || "get").toLowerCase();
      // Safe to retry idempotent methods + writes for known-safe endpoints
      // (creating a bank with the same payload twice is fine — server returns 200 either way).
      const writeRetryAllowed =
        method === "post" && /\/interview\/(banks|extract|sessions|tts|trash)/.test(cfg.url || "");
      const canRetry = method === "get" || writeRetryAllowed;
      if (isTransient && canRetry && cfg.__retryCount < 3) {
        cfg.__retryCount += 1;
        const backoff = 500 * Math.pow(2, cfg.__retryCount - 1); // 500ms, 1s, 2s
        await new Promise((r) => setTimeout(r, backoff));
        try { return await api.request(cfg); } catch (e2) { return Promise.reject(e2); }
      }
    }
    return Promise.reject(err);
  }
);


/**
 * Safely extract a user-friendly error message from any axios error / API response.
 * Handles FastAPI 422 validation errors (which return `detail` as an array of
 * {loc, msg, type} objects) — those would otherwise stringify to
 * "[object Object],[object Object]" via implicit JS coercion.
 */
export function extractErr(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    return d
      .map((it) => (typeof it === "string" ? it : it?.msg || it?.message || JSON.stringify(it)))
      .filter(Boolean)
      .join("; ") || fallback;
  }
  if (d && typeof d === "object") {
    return d.msg || d.message || JSON.stringify(d);
  }
  const m = err?.response?.data?.message || err?.message;
  return (typeof m === "string" && m) || fallback;
}
